import { z } from "zod";
import { aiFocusSchema, aiModelChoiceSchema, templateButtonSchema, uuidSchema } from "./domain";
import { templateVariableSchema } from "./followup";

const jobBaseSchema = z
  .object({
    workspaceId: uuidSchema,
    correlationId: uuidSchema,
    idempotencyKey: z.string().min(8).max(256),
  })
  .strict();

export const providerEventJobSchema = jobBaseSchema
  .extend({ providerEventId: uuidSchema })
  .strict();
export type ProviderEventJob = z.infer<typeof providerEventJobSchema>;

export const executeProposalJobSchema = jobBaseSchema.extend({ proposalId: uuidSchema }).strict();
export type ExecuteProposalJob = z.infer<typeof executeProposalJobSchema>;

export const executeActionJobSchema = jobBaseSchema.extend({ actionId: uuidSchema }).strict();
export type ExecuteActionJob = z.infer<typeof executeActionJobSchema>;

export const generateAiProposalJobSchema = jobBaseSchema
  .extend({
    leadId: uuidSchema,
    triggerMessageId: uuidSchema.nullable(),
    /** Automatic jobs follow a lead's message and stop if it was already answered. */
    origin: z.enum(["manual", "automatic"]).default("manual"),
    focus: aiFocusSchema.default("all"),
    operatorInstructions: z.string().max(2_000).nullable().default(null),
    /** "Correggi con AI": the pending proposal to rewrite (ADR 0051). */
    reviseProposalId: uuidSchema.nullable().default(null),
    /** The model the operator asked for (ADR 0077); missing or null: the default one. */
    model: aiModelChoiceSchema.nullable().optional(),
    /** After a recorded call is transcribed (ADR 0067): runs even if we wrote last. */
    triggerCallId: uuidSchema.nullable().optional(),
    /**
     * "Genera ora" (ADR 0072): an automatic job someone asked for, so the rules
     * that spare proposals nobody asked for do not apply (ADR 0078).
     */
    startedNow: z.boolean().optional(),
  })
  .strict();
export type GenerateAiProposalJob = z.infer<typeof generateAiProposalJobSchema>;

/** The spam filter on a new contact's first messages (ADR 0093). */
export const spamCheckJobSchema = jobBaseSchema.extend({ leadId: uuidSchema }).strict();
export type SpamCheckJob = z.infer<typeof spamCheckJobSchema>;

export const downloadMediaJobSchema = jobBaseSchema.extend({ attachmentId: uuidSchema }).strict();
export type DownloadMediaJob = z.infer<typeof downloadMediaJobSchema>;

export const transcribeAttachmentJobSchema = jobBaseSchema
  .extend({ attachmentId: uuidSchema })
  .strict();
export type TranscribeAttachmentJob = z.infer<typeof transcribeAttachmentJobSchema>;

export const transcribeCallJobSchema = jobBaseSchema.extend({ callId: uuidSchema }).strict();
export type TranscribeCallJob = z.infer<typeof transcribeCallJobSchema>;

export const templateJobSchema = jobBaseSchema
  .extend({
    connectionId: uuidSchema,
    templateId: uuidSchema.nullable(),
    /** "edit" changes a template on Meta; "delete" removes it from Meta (ADR 0061). */
    operation: z.enum(["submit", "sync", "edit", "delete"]),
    /**
     * "edit": the new content. It is written to `pl_template` only once Meta
     * accepts it, so a sync running meanwhile cannot undo or resend it.
     */
    edit: z
      .object({
        body: z.string().min(1).max(4_096),
        header: z.string().max(1_024).nullable(),
        footer: z.string().max(60).nullable(),
        buttons: z.array(templateButtonSchema).max(10),
        variables: z.array(templateVariableSchema).max(10),
      })
      .strict()
      .nullable()
      .default(null),
  })
  .strict();
export type TemplateJob = z.infer<typeof templateJobSchema>;

export const leadAdsSyncJobSchema = jobBaseSchema
  .extend({
    connectionId: uuidSchema,
    trigger: z.enum(["connected", "manual", "settings", "scheduled"]),
  })
  .strict();
export type LeadAdsSyncJob = z.infer<typeof leadAdsSyncJobSchema>;

/** Reads Meta's WhatsApp costs and number status of a connection (ADR 0049). */
export const whatsappCostsJobSchema = jobBaseSchema
  .extend({
    connectionId: uuidSchema,
    trigger: z.enum(["manual", "scheduled"]),
  })
  .strict();
export type WhatsappCostsJob = z.infer<typeof whatsappCostsJobSchema>;

/**
 * Platform job of the API costs (ADR 0062): reads OpenAI's daily costs and splits
 * them among the workspaces, and reads again the cost of finished Apify runs. Not
 * tied to one workspace: OpenAI's project and Apify's account serve all of them.
 */
export const apiCostsJobSchema = z
  .object({
    correlationId: uuidSchema,
    idempotencyKey: z.string().min(8).max(256),
    trigger: z.enum(["manual", "scheduled"]),
  })
  .strict();
export type ApiCostsJob = z.infer<typeof apiCostsJobSchema>;

/** Recomputes the follow-ups of a lead's conversations, or of the whole workspace when null. */
export const followupJobSchema = jobBaseSchema
  .extend({
    leadId: uuidSchema.nullable(),
    trigger: z.enum(["member", "lead_changed", "scheduled"]),
  })
  .strict();
export type FollowupJob = z.infer<typeof followupJobSchema>;

/**
 * Prepares follow-ups of the day with the AI (ADR 0055): all the workspace's
 * ("scheduled", the morning pass) or one ("member": "Prepara con AI";
 * "revise" / "regenerate" on its pending preparation).
 */
export const followupPrepareJobSchema = jobBaseSchema
  .extend({
    followupId: uuidSchema.nullable(),
    trigger: z.enum(["scheduled", "member", "revise", "regenerate"]),
    operatorInstructions: z.string().max(2_000).nullable().default(null),
    /** "Correggi con AI": the pending preparation to rewrite. */
    reviseProposalId: uuidSchema.nullable().default(null),
    /** "Modifica con AI" (ADR 0061): the card's text to rewrite into a message. */
    baseText: z.string().max(4_096).nullable().default(null),
  })
  .strict();
export type FollowupPrepareJob = z.infer<typeof followupPrepareJobSchema>;

/**
 * The Sityweb drafts of an archived lead (ADR 0065), deleted after a grace
 * period: restoring the lead before it cancels the job and keeps the draft.
 */
export const draftCleanupJobSchema = jobBaseSchema
  .extend({
    leadId: uuidSchema,
    /** The archiving this job belongs to: a lead restored and archived again has a new one. */
    archivedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type DraftCleanupJob = z.infer<typeof draftCleanupJobSchema>;

export const DRAFT_CLEANUP_DELAY_MS = 10 * 60_000;

export function draftCleanupJobId(leadId: string, archivedAt: Date): string {
  return `draft-cleanup__${leadId}__${archivedAt.getTime()}`;
}

export const dataJobSchema = jobBaseSchema.extend({ dataJobId: uuidSchema }).strict();
export type DataJob = z.infer<typeof dataJobSchema>;

export const retentionSweepJobSchema = z
  .object({
    correlationId: uuidSchema,
    requestedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type RetentionSweepJob = z.infer<typeof retentionSweepJobSchema>;
